import { AlertCircleIcon } from 'lucide-react-native';
import React from 'react';
import { z } from 'zod';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { VStack } from '@/components/ui/vstack';

export type GuestIdentity = {
  fullName: string;
  email: string;
  npi: string;
};

export type GuestIdentityErrors = Partial<Record<keyof GuestIdentity, string>>;

export const EMPTY_GUEST_IDENTITY: GuestIdentity = { fullName: '', email: '', npi: '' };

/**
 * The longest name or email the backend stores. A longer value is refused there
 * with a 400 that names no field, so the form stops it first.
 */
const GUEST_TEXT_MAX_LENGTH = 255;

/**
 * The full name only has to be present. The backend stores it as one string and
 * never splits it, and requiring two words would turn away a mononym or a name
 * written without spaces. The NPI is optional, but a value that is present must
 * be the 10 digits a registry lookup can resolve.
 */
const guestIdentitySchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, 'Enter your full name')
    .max(GUEST_TEXT_MAX_LENGTH, `Use ${GUEST_TEXT_MAX_LENGTH} characters or fewer`),
  email: z
    .string()
    .trim()
    .email('Enter a valid email address')
    .max(GUEST_TEXT_MAX_LENGTH, `Use ${GUEST_TEXT_MAX_LENGTH} characters or fewer`),
  npi: z
    .string()
    .trim()
    .regex(/^(\d{10})?$/, 'NPI must be 10 digits'),
});

/**
 * Validates what an anonymous respondent typed and returns either the cleaned
 * values to send or the inline error for each field that failed. A blank NPI
 * comes back as `undefined` so it is omitted from the request rather than sent
 * as an empty string.
 */
export function validateGuestIdentity(
  identity: GuestIdentity
):
  | { success: true; data: { fullName: string; email: string; npi?: string } }
  | { success: false; errors: GuestIdentityErrors } {
  const parsed = guestIdentitySchema.safeParse(identity);

  if (!parsed.success) {
    const errors: GuestIdentityErrors = {};

    for (const issue of parsed.error.issues) {
      const field = issue.path[0] as keyof GuestIdentity;

      errors[field] ??= issue.message;
    }

    return { success: false, errors };
  }

  const { fullName, email, npi } = parsed.data;

  return { success: true, data: { fullName, email, npi: npi || undefined } };
}

type GuestIdentityFieldsProps = {
  value: GuestIdentity;
  errors: GuestIdentityErrors;
  onChange: (field: keyof GuestIdentity, value: string) => void;
};

/**
 * The identity a guest gives before answering: full name and email are
 * required, NPI is optional but nudged. The NPI is the one that lets us match
 * the respondent to a registry record and create a validated oncologist account
 * without a separate verification step, so the helper says so instead of
 * leaving the optional label to read as "not worth filling in".
 */
export function GuestIdentityFields({ value, errors, onChange }: GuestIdentityFieldsProps) {
  return (
    <VStack space="md">
      <FormControl isInvalid={!!errors.fullName} isRequired className="gap-2">
        <FormControlLabel>
          <FormControlLabelText>Full name</FormControlLabelText>
        </FormControlLabel>

        <Input>
          <InputField
            testID="guest-full-name"
            enterKeyHint="next"
            autoCapitalize="words"
            autoCorrect={false}
            autoComplete="name"
            textContentType="name"
            maxLength={GUEST_TEXT_MAX_LENGTH}
            value={value.fullName}
            onChangeText={(text) => onChange('fullName', text)}
            accessibilityLabel="Full name, required"
          />
        </Input>

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.fullName}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <FormControl isInvalid={!!errors.email} isRequired className="gap-2">
        <FormControlLabel>
          <FormControlLabelText>Email</FormControlLabelText>
        </FormControlLabel>

        <Input>
          <InputField
            testID="guest-email"
            inputMode="email"
            enterKeyHint="next"
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            autoComplete="email"
            maxLength={GUEST_TEXT_MAX_LENGTH}
            value={value.email}
            onChangeText={(text) => onChange('email', text)}
            accessibilityLabel="Email, required"
          />
        </Input>

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.email}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <FormControl isInvalid={!!errors.npi} className="gap-2">
        <FormControlLabel>
          <FormControlLabelText>NPI number (optional)</FormControlLabelText>
        </FormControlLabel>

        <Input>
          <InputField
            testID="guest-npi"
            inputMode="numeric"
            enterKeyHint="done"
            autoCorrect={false}
            spellCheck={false}
            maxLength={10}
            value={value.npi}
            onChangeText={(text) => onChange('npi', text)}
            accessibilityLabel="NPI number, optional"
          />
        </Input>

        <FormControlHelper>
          <FormControlHelperText size="xs" className="text-typography-700">
            Recommended, not required. Adding your NPI makes it easier to set up your Primum account
            and confirm you&apos;re an oncologist.
          </FormControlHelperText>
        </FormControlHelper>

        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.npi}</FormControlErrorText>
        </FormControlError>
      </FormControl>
    </VStack>
  );
}
